import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { GlassSurface } from '../GlassSurface'
import { MobileNavDrawer } from './MobileNavDrawer'
import { cn } from '../../lib/cn'
import { primaryNav, platformMenu, headerCtas } from '../../lib/site-nav'
import type { NavLink } from '../../lib/site-nav'
import { formatStars, useGithubStars, NAV_GITHUB_STARS_FLAG } from '../../lib/github-stars'
import { subscribeFeatureFlagVariant } from '../../lib/analytics'

// Lazy-load the brand-assets overlay so the (heavy: motion + tailwind-merge
// + WebGL shaders) BrandMenu module never lands in the shared nav chunk
// that every page preloads. The overlay is mounted only after the user
// right-clicks the logo, so first paint and per-page initial JS shrink a
// lot and there is no Suspense boundary on the critical path.
const BrandMenuOverlay = lazy(() =>
  import('./BrandMenu').then((m) => ({ default: m.BrandMenuOverlay })),
)

/**
 * True when the element's centre point sits over any region tagged
 * `data-nav-dark`. Lets each nav item flip to white only where the backdrop
 * beneath it is actually dark — the Apple adaptive-nav trick, hit-tested per
 * item rather than for the whole bar.
 */
function isOverDark(el: HTMLElement | null): boolean {
  if (!el) return false
  const r = el.getBoundingClientRect()
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2
  const darks = Array.from(
    document.querySelectorAll<HTMLElement>('[data-nav-dark]'),
  )
  for (const d of darks) {
    const dr = d.getBoundingClientRect()
    if (cx >= dr.left && cx <= dr.right && cy >= dr.top && cy <= dr.bottom) {
      return true
    }
  }
  return false
}

/**
 * A `clip-path` inset that exposes only the horizontal slice of the logo that
 * overlaps a dark region — so the wordmark splits to white exactly where the
 * dark backdrop begins, mid-letter and all (the Apple effect, per-pixel).
 */
function darkClipForLogo(el: HTMLElement | null): string {
  const HIDDEN = 'inset(0 100% 0 0)'
  if (!el) return HIDDEN
  const lr = el.getBoundingClientRect()
  let coverLeft = Infinity
  let coverRight = -Infinity
  const darks = Array.from(
    document.querySelectorAll<HTMLElement>('[data-nav-dark]'),
  )
  for (const d of darks) {
    const dr = d.getBoundingClientRect()
    if (dr.top > lr.bottom || dr.bottom < lr.top) continue // no vertical overlap
    const ol = Math.max(lr.left, dr.left)
    const or = Math.min(lr.right, dr.right)
    if (or > ol) {
      coverLeft = Math.min(coverLeft, ol)
      coverRight = Math.max(coverRight, or)
    }
  }
  if (coverRight <= coverLeft) return HIDDEN
  const insetLeft = Math.max(0, coverLeft - lr.left)
  const insetRight = Math.max(0, lr.right - coverRight)
  return `inset(0px ${insetRight}px 0px ${insetLeft}px)`
}

export function VariantNav({ demoCta }: { demoCta?: NavLink } = {}) {
  const contactCta: NavLink = demoCta ?? headerCtas.contactSales

  // nav-github-stars experiment: prerendered HTML and the control variant are
  // the icon-only link; the `stars` variant adds the live count. The flag
  // resolves client-side after PostHog loads, so control never flickers.
  const [starsVariant, setStarsVariant] = useState<string | undefined>(undefined)
  useEffect(() => subscribeFeatureFlagVariant(NAV_GITHUB_STARS_FLAG, setStarsVariant), [])
  const showStars = starsVariant === 'stars'
  const stars = useGithubStars(showStars)

  const [scrolled, setScrolled] = useState(false)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const [surfaceDark, setSurfaceDark] = useState(false)

  const logoRef = useRef<HTMLAnchorElement>(null)
  const signInRef = useRef<HTMLAnchorElement>(null)
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([])

  const [logoClip, setLogoClip] = useState('inset(0 100% 0 0)')
  const [signInDark, setSignInDark] = useState(false)
  const [linkDark, setLinkDark] = useState<boolean[]>(() => primaryNav.map(() => false))

  const [brandActive, setBrandActive] = useState<{ x: number; y: number } | null>(null)
  const onBrandContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setBrandActive({ x: e.clientX, y: e.clientY })
  }

  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    let raf = 0
    const update = () => {
      setScrolled(window.scrollY > 24)
      setSurfaceDark(isOverDark(surfaceRef.current))
      setLogoClip(darkClipForLogo(logoRef.current))
      setSignInDark(isOverDark(signInRef.current))
      setLinkDark((prev) => {
        const next = linkRefs.current.map((el) => isOverDark(el))
        return next.length === prev.length && next.every((v, i) => v === prev[i])
          ? prev
          : next
      })
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <header className="sticky top-0 z-50 w-full">
      <div
        className={cn(
          'container-wide transition-[padding] duration-500 ease-out',
          scrolled ? 'pt-2' : 'pt-0',
        )}
      >
        <div
          ref={surfaceRef}
          data-nav-surface={surfaceDark ? 'dark' : 'light'}
          className={cn(
            'relative flex items-center justify-between transition-all duration-500 ease-out',
            scrolled ? 'h-14 px-3 md:px-5' : 'h-16 px-0',
          )}
        >
          {/* Liquid-glass pill — fades in past 24px of scroll */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 transition-opacity duration-500',
              scrolled ? 'opacity-100' : 'opacity-0',
            )}
          >
            <GlassSurface tintOpacity={surfaceDark ? 0.08 : 0.55} />
          </div>

          <div className="relative flex items-center gap-6 xl:gap-10">
            <a
              ref={logoRef}
              href="/"
              aria-label="LangWatch home"
              onContextMenu={onBrandContextMenu}
              className="relative inline-flex shrink-0 items-center"
            >
              <img src="/logo-black.svg" alt="LangWatch" className="h-6 w-auto" />
              {/* Same logo whitened via filter (so it stays pixel-aligned with
                  the black one) and clipped to whatever sits over a dark region. */}
              <img
                src="/logo-black.svg"
                alt=""
                aria-hidden
                className="absolute inset-0 h-6 w-auto"
                style={{
                  filter: 'brightness(0) invert(1)',
                  clipPath: logoClip,
                  WebkitClipPath: logoClip,
                }}
              />
            </a>
            <nav className="hidden items-center gap-1 lg:flex">
              {primaryNav.map((item, i) => {
                const linkClass = cn(
                  'rounded-full px-2 py-1.5 text-[13.5px] font-medium transition-colors duration-300 xl:px-3',
                  linkDark[i]
                    ? 'text-white/90 hover:text-white'
                    : 'text-ink-600 hover:text-ink-900',
                )
                if (item.label === 'Platform') {
                  return (
                    <div key={item.label} className="group relative">
                      <a
                        ref={(el) => {
                          linkRefs.current[i] = el
                        }}
                        href={item.href}
                        className={linkClass}
                      >
                        {item.label}
                      </a>
                      {/* dropdown */}
                      <div className="invisible absolute left-0 top-full z-50 pt-3 opacity-0 transition-opacity duration-200 group-hover:visible group-hover:opacity-100">
                        <div className="grid w-[600px] grid-cols-2 gap-1 rounded-2xl border border-ink-100 bg-paper p-3 shadow-xl">
                          {platformMenu.map((p) => {
                            const Icon = p.icon
                            return (
                              <a
                                key={p.label}
                                href={p.href}
                                className="flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-paper-soft"
                              >
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ink-900 text-paper">
                                  {p.iconSrc ? (
                                    <img
                                      src={p.iconSrc}
                                      alt=""
                                      aria-hidden
                                      className="h-[20px] w-[20px] object-contain"
                                    />
                                  ) : (
                                    Icon && <Icon className="h-[18px] w-[18px]" />
                                  )}
                                </span>
                                <span className="min-w-0">
                                  <span className="flex items-center gap-2 text-[13.5px] font-semibold text-ink-900">
                                    {p.label}
                                    {p.badge && (
                                      <span className="rounded-full bg-brand-500/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-brand-700">
                                        {p.badge}
                                      </span>
                                    )}
                                  </span>
                                  <span className="mt-0.5 block text-[12px] leading-snug text-ink-500">
                                    {p.desc}
                                  </span>
                                </span>
                              </a>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )
                }
                const ext = item.external
                  ? { target: '_blank', rel: 'noreferrer' as const }
                  : {}
                return (
                  <a
                    key={item.label}
                    ref={(el) => {
                      linkRefs.current[i] = el
                    }}
                    href={item.href}
                    {...ext}
                    className={cn(linkClass, item.wide && 'hidden xl:inline')}
                  >
                    {item.label}
                  </a>
                )
              })}
            </nav>
          </div>
          <div className="relative flex items-center gap-1.5">
            <a
              href={headerCtas.github.href}
              target="_blank"
              rel="noreferrer"
              aria-label={
                showStars
                  ? `Star LangWatch on GitHub (${stars.toLocaleString('en-US')} stars)`
                  : 'GitHub'
              }
              className={cn(
                'inline-flex items-center rounded-full transition-colors duration-300',
                showStars ? 'gap-1.5 py-1.5 pl-1.5 pr-2' : 'p-1.5',
                signInDark
                  ? 'text-white/90 hover:text-white'
                  : 'text-ink-700 hover:text-ink-950',
              )}
            >
              <GithubIcon className="h-[18px] w-[18px]" />
              {showStars && (
                <span className="text-[13px] font-semibold tabular-nums">
                  {formatStars(stars)}
                </span>
              )}
            </a>
            <a
              href={contactCta.href}
              {...(contactCta.external ? { target: '_blank', rel: 'noreferrer' } : {})}
              className={cn(
                'hidden whitespace-nowrap rounded-full px-3 py-2 text-[13.5px] font-medium transition-colors duration-300 lg:inline-flex',
                signInDark
                  ? 'text-white/90 hover:text-white'
                  : 'text-ink-600 hover:text-ink-900',
              )}
            >
              {contactCta.label}
            </a>
            <a
              ref={signInRef}
              href={headerCtas.signIn.href}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'hidden whitespace-nowrap rounded-full px-3 py-2 text-[13.5px] font-medium transition-colors duration-300 lg:inline-flex',
                signInDark
                  ? 'text-white/90 hover:text-white'
                  : 'text-ink-600 hover:text-ink-900',
              )}
            >
              Sign in
            </a>
            <a
              href={headerCtas.signUp.href}
              target="_blank"
              rel="noreferrer"
              className="btn-primary whitespace-nowrap !py-2 !text-[13px]"
            >
              Sign Up
            </a>
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
              className={cn(
                'ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-300 lg:hidden',
                signInDark
                  ? 'text-white/90 hover:bg-white/10 hover:text-white'
                  : 'text-ink-700 hover:bg-ink-100 hover:text-ink-950',
              )}
            >
              <MenuIcon className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </div>
      <MobileNavDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />
      {brandActive && (
        <Suspense fallback={null}>
          <BrandMenuOverlay initialPos={brandActive} onClose={() => setBrandActive(null)} />
        </Suspense>
      )}
    </header>
  )
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  )
}

function GithubIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M12 .5C5.37.5 0 5.78 0 12.29c0 5.2 3.44 9.6 8.21 11.16.6.11.82-.25.82-.56 0-.28-.01-1.02-.02-2-3.34.71-4.04-1.58-4.04-1.58-.55-1.37-1.34-1.74-1.34-1.74-1.09-.73.08-.72.08-.72 1.2.08 1.83 1.22 1.83 1.22 1.07 1.8 2.81 1.28 3.5.98.11-.76.42-1.28.76-1.57-2.67-.3-5.47-1.31-5.47-5.83 0-1.29.47-2.34 1.23-3.17-.12-.3-.53-1.51.12-3.15 0 0 1-.32 3.3 1.21a11.6 11.6 0 0 1 6 0c2.3-1.53 3.3-1.21 3.3-1.21.65 1.64.24 2.85.12 3.15.77.83 1.23 1.88 1.23 3.17 0 4.53-2.8 5.53-5.48 5.82.43.37.81 1.1.81 2.22 0 1.6-.01 2.9-.01 3.29 0 .31.22.68.83.56C20.56 21.88 24 17.48 24 12.29 24 5.78 18.63.5 12 .5Z" />
    </svg>
  )
}
